import React, { useState, useEffect } from "react";
import styles from "./CreateStoreModule.module.scss";
import TextField from "../../components/TextField/TextField";
import Button from "../../components/Button/Button";
import { Form, FormikProvider, useFormik } from "formik";
import { createStore, Marketplace, getStoreList, Store } from "../../ApiService";
import { useMutation, useQueryClient, useQuery } from "react-query";
import Modal from "../../components/Modal/Modal";
import useTokenValidation from "../StoreSettings/useTokenValidation";
import useOzonCredentialsValidation from "../StoreSettings/useOzonCredentialsValidation";
import { useMarketplace } from "../../contexts/MarketplaceContext";

export type CreateStore = {
  name: string;
  prompt: Record<string, any>;
  token?: string; // для WB
  clientId?: string; // для Ozon
  apiKey?: string; // для Ozon
  marketplace: Marketplace;
  generateEmptyFeedback: boolean;
  smartAutoReplies?: boolean;
};

function getInitialValues(marketplace: Marketplace): CreateStore {
  return {
    name: "",
    prompt: {},
    token: marketplace === 'wb' ? "" : undefined,
    clientId: marketplace === 'ozon' ? "" : undefined,
    apiKey: marketplace === 'ozon' ? "" : undefined,
    marketplace,
    generateEmptyFeedback: false,
    smartAutoReplies: false,
  };
}

export type CreateStoreModuleProps = {
  isOpen: boolean;
  onClose: () => void;
};

const CreateStoreModule: React.FC<CreateStoreModuleProps> = ({
  isOpen,
  onClose,
}) => {
  const { currentMarketplace, isWB } = useMarketplace();
  const [selectedTemplateStore, setSelectedTemplateStore] = useState<string>("");
  const queryClient = useQueryClient();

  // Получаем магазины другого маркетплейса для копирования настроек
  const otherMarketplace: Marketplace = isWB ? 'ozon' : 'wb';
  const { data: templateStores } = useQuery(
    ["stores", otherMarketplace],
    () => getStoreList(otherMarketplace),
    {
      enabled: isOpen, // Загружаем только когда модал открыт
    }
  );

  const mutation = useMutation((values: CreateStore) => createStore(values), {
    onSuccess: () => {
      queryClient.refetchQueries(["stores", currentMarketplace]).then(onClose);
    },
  });

  const formik = useFormik({
    initialValues: getInitialValues(currentMarketplace),
    enableReinitialize: true, // Перезапускать форму при смене маркетплейса
    onSubmit: (values) => mutation.mutate(values),
  });

  const { values, handleChange, setFieldValue } = formik;

  // Логика валидации в зависимости от маркетплейса
  const { isTokenValid, handleTokenChange } = useTokenValidation(setFieldValue);
  const { isCredentialsValid, handleClientIdChange, handleApiKeyChange } =
    useOzonCredentialsValidation(setFieldValue, { clientId: values.clientId, apiKey: values.apiKey });

  const createAllowed = isWB
    ? values.name && values.token && isTokenValid
    : values.name && values.clientId && values.apiKey && isCredentialsValid;

  // Обработчик выбора шаблона магазина
  const handleTemplateSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const storeId = event.target.value;
    setSelectedTemplateStore(storeId);

    if (storeId && templateStores) {
      const templateStore = templateStores.find((store: Store) => store.id === storeId);
      if (templateStore) {
        setFieldValue('name', templateStore.name);
        setFieldValue('generateEmptyFeedback', templateStore.generateEmptyFeedback);

        // Копируем все поля промпта
        const copiedPrompt = { ...templateStore.prompt };
        setFieldValue('prompt', copiedPrompt);

        // НЕ копируем токен/ключи - пользователь должен ввести новые для другого маркетплейса
      }
    } else {
      // При выборе "Создать с нуля" сбрасываем поля
      setFieldValue('name', "");
      setFieldValue('prompt', {});
      setFieldValue('generateEmptyFeedback', false);
    }
  };

  // Сброс при закрытии модала
  useEffect(() => {
    if (!isOpen) {
      setSelectedTemplateStore("");
    }
  }, [isOpen]);

  const marketplaceName = isWB ? "Wildberries" : "Ozon";
  const otherMarketplaceName = isWB ? "Ozon" : "Wildberries";

  return (
    <Modal isOpen={isOpen} size="large" onClose={onClose}>
      <FormikProvider value={formik}>
        <Form>
          <div className={styles.fieldsContainer}>
            <p className={styles.h3Font}>Создание магазина {marketplaceName}</p>

            {templateStores && templateStores.length > 0 && (
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Скопировать настройки из магазина {otherMarketplaceName} (опционально)
                </label>
                <select
                  className="w-full rounded border-2 border-gray-300 p-2"
                  value={selectedTemplateStore}
                  onChange={handleTemplateSelect}
                >
                  <option value="">Создать с нуля</option>
                  {templateStores.map((store: Store) => (
                    <option key={store.id} value={store.id}>
                      {store.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  При выборе скопируется название и промпты, но потребуется новый API ключ
                </p>
              </div>
            )}

            <TextField
              label="Название вашего магазина"
              name="name"
              type="text"
              placeholder="Введите название"
              onChange={handleChange}
              value={values.name}
              autoComplete="off"
            />

            {isWB ? (
              <div className="w-full relative">
                <TextField
                  label="Токен вашего магазина (должен включать разделы Контент, Вопросы и Отзывы)"
                  name="token"
                  type="text"
                  placeholder="Введите токен"
                  onChange={handleTokenChange}
                  value={values.token || ""}
                  autoComplete="off"
                  className="pr-4"
                  valid={!!values.token ? isTokenValid : undefined}
                />
              </div>
            ) : (
              <>
                <TextField
                  label="ID кабинета Ozon (Client ID)"
                  name="clientId"
                  type="text"
                  placeholder="Введите Client ID"
                  onChange={handleClientIdChange}
                  value={values.clientId || ""}
                  autoComplete="off"
                  valid={!!values.clientId && !!values.apiKey ? isCredentialsValid : undefined}
                />
                <TextField
                  label="API ключ вашего магазина Ozon"
                  name="apiKey"
                  type="text"
                  placeholder="Введите API ключ"
                  onChange={handleApiKeyChange}
                  value={values.apiKey || ""}
                  autoComplete="off"
                  valid={!!values.clientId && !!values.apiKey ? isCredentialsValid : undefined}
                />
              </>
            )}
          </div>
          <div className="flex flex-col items-center mt-8">
            <Button
              type="submit"
              title="Создать магазин"
              size="large"
              status={createAllowed ? "enabled" : "disabled"}
            />
          </div>
        </Form>
      </FormikProvider>
    </Modal>
  );
};

export default CreateStoreModule;
